/**
 * One socket, one request, every chart group on the layout.
 *
 * Each `ChartGroupView` used to own a `QvWebSocketClient`, connect it, and send
 * a request carrying its own group (plus its PIP). A two-pane layout was
 * therefore two sockets and two requests, and the server read the same symbol
 * file once per request — which is the cost that matters, not the round trip:
 * measured on real AAPL bars, four groups on one symbol are **31.6 ms with
 * separate reads and 11.2 ms sharing one**, because `base_frame` is 7.6 ms warm
 * and the work above it is ~1 ms.
 *
 * The wire has always supported it. `build_bundle` packs `num_groups` with a
 * per-group id precisely so one response can carry several, and the transport
 * client already demultiplexes: `subKeyByGroup`, `lastByGroup`, and a
 * `chartGroupId` on every listener callback. Only the React layer insisted on
 * one client each.
 *
 * ## What this is, and what it deliberately is not
 *
 * It is a registry keyed by socket **and layout**, holding one client and the
 * current request of every mounted group. Registering, unregistering or
 * refreshing rebuilds the combined request and sends it once.
 *
 * It is **not** a cache and it does not interpret a single frame. Everything
 * that arrives is handed to the member that owns that `chartGroupId`, so the
 * view's existing routing — including `routePip`, which already separates a
 * chart's frames from its PIP's — keeps working untouched.
 *
 * ## The trade this makes, stated rather than discovered
 *
 * `ChartManager` names a channel by the hash of the whole request, so groups
 * sharing a request share a channel: changing the symbol on one pane
 * invalidates and rebuilds **all** of them. That is the cost. It is worth
 * paying because the rebuild is now one read rather than N — the 65% saving
 * above is exactly what pays for it — and because the alternative, a channel
 * per group, is what made two panes two sockets in the first place.
 *
 * ## Coalescing, and why it is a microtask rather than a timer
 *
 * Several groups mount in the same React commit, and each registers as it
 * builds. Sending on every registration would produce N requests again, one of
 * them complete. So a send is scheduled on the microtask queue and collapses:
 * whatever is registered when it runs goes in one request. A timer would work
 * too and would add latency nobody asked for — the mounts are already in one
 * task.
 */

import type {
  IndicatorRequest,
  TransportClient,
  TransportListeners,
} from "@qufolio/transport";

/** One group's contribution: the ids it owns, and the request entries for them. */
export interface HubMember {
  /**
   * Every `chartGroupId` this member should receive frames for.
   *
   * A chart owns two when its PIP is a chart — its own and the PIP's — and the
   * view already tells them apart. Listed rather than derived so the hub needs
   * to know nothing about how a PIP id is formed.
   */
  ids: number[];
  /** This member's chart groups, in the order the server should see them. */
  groups: IndicatorRequest["chartGroups"];
  listeners: TransportListeners;
}

type Factory = (listeners: TransportListeners) => TransportClient;

/** Everything sharing one socket: the client, its members, and its send state. */
interface Hub {
  client: TransportClient;
  connected: Promise<void> | null;
  members: Map<string, HubMember>;
  /** `chartGroupId` → member key, rebuilt whenever membership changes. */
  owner: Map<number, string>;
  layoutId: string;
  userId: string | number;
  scheduled: boolean;
  /**
   * Groups joined since the last send, which the next one asks to reload.
   *
   * A join is a new surface with nothing drawn — the view joins from the effect
   * that builds one — while every other member's surface still holds its bars.
   * Only the hub can tell the two apart; see `RequestOptions.reload`.
   */
  reload: Set<number>;
  /** Retry number for a first connect that failed; see `retryConnect`. */
  retryAttempt: number;
  retryTimer: ReturnType<typeof setTimeout> | null;
  /** The close after the last member left, cancelled by a join; see `leave`. */
  closing: ReturnType<typeof setTimeout> | null;
}

const hubs = new Map<string, Hub>();

/**
 * Dispatch one callback to whoever owns the group it names.
 *
 * A frame for an unknown id is dropped rather than broadcast. That is the
 * important half: broadcasting would append another group's series onto this
 * surface — a weekly line drawn onto a daily chart, with no error — which is
 * the exact failure `routePip` exists to prevent, one level up.
 */
function dispatch(hub: Hub, id: unknown, call: (l: TransportListeners) => void): void {
  const numeric = Number(id);
  const key = hub.owner.get(numeric);
  if (key === undefined) return;
  const member = hub.members.get(key);
  if (member) call(member.listeners);
}

function reindex(hub: Hub): void {
  hub.owner.clear();
  for (const [key, member] of hub.members) {
    for (const id of member.ids) hub.owner.set(Number(id), key);
  }
}

function hubFor(
  socketKey: string,
  layoutId: string,
  userId: string | number,
  factory: Factory,
): Hub {
  const key = `${socketKey} ${layoutId}`;
  const existing = hubs.get(key);
  if (existing) return existing;

  const hub: Hub = {
    client: undefined as unknown as TransportClient,
    connected: null,
    members: new Map(),
    owner: new Map(),
    layoutId,
    userId,
    scheduled: false,
    reload: new Set(),
    retryAttempt: 0,
    retryTimer: null,
    closing: null,
  };
  // The listeners are the hub's, permanently — members come and go beneath
  // them. A per-member listener set would have to be swapped on the live
  // client, which is the race this indirection removes.
  hub.client = factory({
    onBulkLoad: (load) => dispatch(hub, load.columns.chartGroupId, (l) => l.onBulkLoad?.(load)),
    onAppend: (frame) => dispatch(hub, frame.chartGroupId, (l) => l.onAppend?.(frame)),
    onLive: (info) => dispatch(hub, info.chartGroupId, (l) => l.onLive?.(info)),
    // Shapes (`qf.geometry`) go to the owner like a frame: they describe one
    // chart's bars, and a set routed to the neighbour would draw its lines
    // across the wrong symbol.
    onGeometry: (frame) => dispatch(hub, frame.chartGroupId, (l) => l.onGeometry?.(frame)),
    // To the owner only, like a frame — the gap is in that chart's bars. A
    // member that cannot rebuild still hears about it, as the error the client
    // itself would have raised: the hub defining `onGap` means the client's own
    // fallback never runs, so it has to be kept here.
    onGap: (gap) =>
      dispatch(hub, gap.chartGroupId, (l) =>
        l.onGap
          ? l.onGap(gap)
          : l.onError?.({
              source: "server",
              message: "bars were missed since the chart loaded — reload the chart to refill it",
              chartGroupId: gap.chartGroupId,
            }),
      ),
    onError: (error) => {
      // An error without a group is the socket's, not a chart's, so it goes to
      // everyone: a dropped connection is every pane's problem.
      if (error.chartGroupId === undefined) {
        for (const m of hub.members.values()) m.listeners.onError?.(error);
        return;
      }
      dispatch(hub, error.chartGroupId, (l) => l.onError?.(error));
    },
    // Status is the socket's by definition and has no group.
    onStatus: (status) => {
      for (const m of hub.members.values()) m.listeners.onStatus?.(status);
    },
  });
  hubs.set(key, hub);
  return hub;
}

/** Build the one request covering every registered member, in stable order. */
export function combinedRequest(hub: {
  layoutId: string;
  userId: string | number;
  members: Map<string, HubMember>;
}): IndicatorRequest {
  const chartGroups: IndicatorRequest["chartGroups"] = [];
  // Sorted by member key so the request — and therefore `channel_key`'s hash —
  // does not change merely because two panes mounted in a different order.
  for (const key of [...hub.members.keys()].sort()) {
    chartGroups.push(...(hub.members.get(key)?.groups ?? []));
  }
  return { layoutId: hub.layoutId, userId: hub.userId, chartGroups };
}

async function flush(hub: Hub): Promise<void> {
  hub.scheduled = false;
  if (hub.members.size === 0) return;
  if (!hub.connected) hub.connected = hub.client.connect();
  const attempt = hub.connected;
  try {
    await attempt;
  } catch (cause) {
    // **Forgotten, and retried from here.** The client reconnects a dropped
    // socket by itself, but only one that has carried a request — a first
    // connect that fails is left for the caller to answer, and this is the
    // caller. Cached, the rejection was awaited by every later send, so a chart
    // opened against a sleeping server stayed dead until a reload. Compared
    // before clearing: a flush that raced ahead may already hold a newer one.
    if (hub.connected === attempt) hub.connected = null;
    retryConnect(hub);
    throw cause;
  }
  hub.retryAttempt = 0;
  // Taken only once the connect has succeeded, so a failed one still owes it.
  // Handed over whether or not this send then gets through: the client keeps
  // it for the replay.
  const reload = [...hub.reload];
  hub.reload.clear();
  await hub.client.request(combinedRequest(hub), { reload });
}

/**
 * The transport's schedule for a socket that dropped, repeated for one that
 * never opened: doubling from half a second, capped at 30s, dithered into
 * `[d/2, d]` so a layout's worth of browsers does not return in lockstep.
 */
function retryDelay(attempt: number): number {
  const ceiling = Math.min(500 * 2 ** attempt, 30_000);
  return ceiling / 2 + Math.random() * (ceiling / 2);
}

function retryConnect(hub: Hub): void {
  if (hub.retryTimer !== null) return;
  hub.retryTimer = setTimeout(() => {
    hub.retryTimer = null;
    schedule(hub);
  }, retryDelay(hub.retryAttempt++));
}

function cancelRetry(hub: Hub): void {
  if (hub.retryTimer === null) return;
  clearTimeout(hub.retryTimer);
  hub.retryTimer = null;
}

function schedule(hub: Hub): void {
  if (hub.scheduled) return;
  hub.scheduled = true;
  // A microtask, so every group mounting in one React commit lands in one
  // request. `void` because a send failure is reported through `onError`, and
  // an unhandled rejection here would be a second report of the same thing.
  queueMicrotask(() => void flush(hub).catch(() => undefined));
}

export interface JoinOptions {
  /** Distinguishes sockets: the URL, or a fixture's own marker. */
  socketKey: string;
  layoutId: string;
  userId: string | number;
  /** Stable per mounted group — its own id is the natural choice. */
  memberKey: string;
  member: HubMember;
  /** Builds the client the first time this socket is needed. */
  factory: Factory;
}

export interface JoinHandle {
  /** Re-send after this member's request changed. */
  refresh(member: HubMember): void;
  /**
   * Remove this member. When it was the last one the socket closes one task
   * later, unless a member joins first — which is what a symbol change does.
   */
  leave(): void;
}

/**
 * Add one group to the shared socket, and send.
 *
 * Idempotent per `memberKey`: a re-register replaces that member rather than
 * adding a second, which is what makes a React effect that re-runs on a symbol
 * change safe to call this from.
 */
export function join(options: JoinOptions): JoinHandle {
  const { socketKey, layoutId, userId, memberKey, member, factory } = options;
  const hub = hubFor(socketKey, layoutId, userId, factory);
  // A join straight after the last leave is a symbol change, not an unmount:
  // keep the socket (see `leave`).
  if (hub.closing !== null) {
    clearTimeout(hub.closing);
    hub.closing = null;
  }
  hub.members.set(memberKey, member);
  for (const id of member.ids) hub.reload.add(id);
  reindex(hub);
  schedule(hub);

  return {
    refresh(next: HubMember) {
      hub.members.set(memberKey, next);
      reindex(hub);
      schedule(hub);
    },
    leave() {
      hub.members.delete(memberKey);
      reindex(hub);
      if (hub.members.size === 0) {
        // Last one out closes the socket and drops the hub, so a layout that
        // unmounts entirely leaves nothing running and the next mount builds a
        // fresh client rather than reusing a disconnected one.
        //
        // **One task later, not now.** A symbol change rebuilds the chart: its
        // effect's cleanup leaves and the same commit's effect joins again. On
        // a one-chart layout that leave was the last, so closing here threw the
        // socket away and the join opened a new one — which had to sign in
        // again before the server would read the request (measured 2026-09-25:
        // 2.2–3.6 s of the chart's wait). A join before the timer fires cancels
        // it and the request goes out on the socket that is already open. See
        // `docs/plans/auth-hot-path.md` §4.
        cancelRetry(hub);
        const key = `${socketKey} ${layoutId}`;
        hub.closing = setTimeout(() => {
          hub.closing = null;
          if (hub.members.size > 0) return;
          hub.client.disconnect();
          if (hubs.get(key) === hub) hubs.delete(key);
        }, 0);
        return;
      }
      // Still occupied: re-send, because the request the server is holding
      // names a group nobody is listening for any more.
      schedule(hub);
    },
  };
}

/** Test seam: forget every hub. Never called by the app. */
export function resetHubs(): void {
  for (const hub of hubs.values()) {
    cancelRetry(hub);
    if (hub.closing !== null) clearTimeout(hub.closing);
    hub.client.disconnect();
  }
  hubs.clear();
}
